import { PhoneCodes, type PhoneCode } from '@/constants/phone-codes'

/**
 * Перевіряє nextDigitPattern (для +7 - Казахстан/Росія)
 */
function matchesNextDigitPattern(
  country: PhoneCode,
  digitsOnly: string,
  codeLength: number
): boolean | null {
  if (!country.nextDigitPattern || digitsOnly.length < codeLength + 1) {
    return null
  }

  const nextDigit = digitsOnly[codeLength]
  if (!nextDigit) {
    return null
  }

  return country.nextDigitPattern.includes(nextDigit)
}

/**
 * Перевіряє areaCodes (для +1 - США/Канада)
 */
function matchesAreaCodes(
  country: PhoneCode,
  digitsOnly: string,
  codeLength: number
): boolean | null {
  if (!country.areaCodes) {
    return null
  }

  if (digitsOnly.length < codeLength + 3) {
    return false
  }

  const areaCode = digitsOnly.substring(codeLength, codeLength + 3)
  return country.areaCodes.includes(areaCode)
}

/**
 * Перевіряє довжину номера: totalLength (фіксована) або minLength/maxLength (діапазон)
 */
function matchesLength(
  country: PhoneCode,
  digitsOnly: string,
  codeLength: number
): boolean | null {
  if (country.totalLength != null) {
    return digitsOnly.length <= country.totalLength
  }

  const minLen = country.minLength
  const maxLen = country.maxLength
  if (minLen === undefined && maxLen === undefined) {
    return null
  }

  const numberLength = digitsOnly.length - codeLength
  if (numberLength === 0) {
    return true
  }

  if (minLen !== undefined && numberLength < minLen) {
    return false
  }

  if (maxLen !== undefined && numberLength > maxLen) {
    return false
  }

  return true
}

/**
 * Перевіряє, чи країна відповідає правилам визначення на основі введених цифр
 * @param country - Країна для перевірки
 * @param digitsOnly - Цифри номера без символів
 * @param codeLength - Довжина коду країни
 * @returns true якщо країна відповідає правилам, false якщо ні
 */
function matchesCountryRules(
  country: PhoneCode,
  digitsOnly: string,
  codeLength: number
): boolean {
  // Перевірка nextDigitPattern
  const nextDigitMatch = matchesNextDigitPattern(country, digitsOnly, codeLength)
  if (nextDigitMatch !== null) {
    return nextDigitMatch
  }

  // Перевірка areaCodes
  const areaCodeMatch = matchesAreaCodes(country, digitsOnly, codeLength)
  if (areaCodeMatch !== null) {
    return areaCodeMatch
  }

  // Перевірка довжини (totalLength або minLength/maxLength)
  const lengthMatch = matchesLength(country, digitsOnly, codeLength)
  if (lengthMatch !== null) {
    return lengthMatch
  }

  return false
}

/**
 * Знаходить країну серед країн з однаковим кодом на основі правил визначення
 * @param matchingCountries - Масив країн з однаковим кодом
 * @param digitsOnly - Цифри номера без символів
 * @param codeLength - Довжина коду
 * @returns PhoneCode або null
 */
export function selectCountryFromMatches(
  matchingCountries: PhoneCode[],
  digitsOnly: string,
  codeLength: number
): PhoneCode | null {
  if (matchingCountries.length === 0) {
    return null
  }

  // Якщо тільки одна країна - повертаємо її
  if (matchingCountries.length === 1) {
    return matchingCountries[0] ?? null
  }

  // Шукаємо країну, яка відповідає правилам
  const matchingCountry = matchingCountries.find((country) =>
    matchesCountryRules(country, digitsOnly, codeLength)
  )

  if (matchingCountry) {
    return matchingCountry
  }

  // Якщо ніхто не підходить — спочатку країна без areaCodes (наприклад US для +1), інакше перша зі списку.
  const fallback = matchingCountries.find((c) => !c.areaCodes) ?? matchingCountries[0]
  return fallback ?? null
}

/**
 * Перевіряє, чи є більш довгий код, який також відповідає введеним цифрам
 * @param codeLength - Довжина поточного коду
 * @param digitsOnly - Всі цифри номера
 * @returns true якщо є більш довгий код, false якщо ні
 */
export function hasLongerMatchingCode(codeLength: number, digitsOnly: string): boolean {
  if (codeLength >= digitsOnly.length) {
    return false
  }

  const longerCode = digitsOnly.substring(0, codeLength + 1)
  const longerMatches = PhoneCodes.filter((c) => c.phoneCode === longerCode)
  return longerMatches.length > 0
}

/**
 * Перевіряє, чи введені цифри є лише префіксом довшого коду (наприклад "38" для 380, 381).
 * У такому разі не визначаємо країну — потрібне точне збігання повного коду.
 */
function isPrefixOfLongerCode(digitsOnly: string): boolean {
  return PhoneCodes.some(
    (c) => c.phoneCode.length > digitsOnly.length && c.phoneCode.startsWith(digitsOnly)
  )
}

/**
 * Знаходить країну по коду телефону серед всіх можливих кодів.
 * Спочатку перевіряє точне збігання коду; якщо є — повертає країну.
 * Якщо точного збігання немає і введено лише префікс довшого коду — повертає null.
 * @param digitsOnly - Цифри номера без символів
 * @returns PhoneCode або null якщо не знайдено
 */
export function findCountryByCode(digitsOnly: string): PhoneCode | null {
  const exactMatches = PhoneCodes.filter((c) => c.phoneCode === digitsOnly)
  if (exactMatches.length > 0) {
    return selectCountryFromMatches(exactMatches, digitsOnly, digitsOnly.length)
  }
  if (isPrefixOfLongerCode(digitsOnly)) {
    return null
  }

  let detectedCountry: PhoneCode | null = null
  let maxMatchLength = 0

  const maxCodeLength = Math.min(7, digitsOnly.length)
  for (let codeLength = maxCodeLength; codeLength >= 1; codeLength--) {
    const potentialCode = digitsOnly.substring(0, codeLength)

    const matchingCountries = PhoneCodes.filter((c) => c.phoneCode === potentialCode)

    if (matchingCountries.length === 0) {
      continue
    }

    // Якщо є більш довгий код, продовжуємо пошук
    if (hasLongerMatchingCode(codeLength, digitsOnly)) {
      continue
    }

    // Використовуємо універсальну функцію вибору країни
    const selectedCountry = selectCountryFromMatches(matchingCountries, digitsOnly, codeLength)

    if (selectedCountry && codeLength > maxMatchLength) {
      detectedCountry = selectedCountry
      maxMatchLength = codeLength

      // Якщо знайшли країну з правилами, яка точно відповідає - зупиняємо пошук
      const hasLengthRule = selectedCountry.totalLength != null
        ? digitsOnly.length >= selectedCountry.totalLength
        : (selectedCountry.minLength !== undefined && digitsOnly.length >= codeLength + selectedCountry.minLength)
      if (
        selectedCountry.nextDigitPattern ||
        selectedCountry.areaCodes ||
        hasLengthRule
      ) {
        break
      }
    }
  }

  return detectedCountry
}

